import { useRetainedState } from "@appretio/shared";
import { useEffect } from "react";
import { useContextViewState } from "../../contextViews/useContextViewState";
import { useSummaryViewState } from "../../summaryViews/useSummaryViewState";
import {
  type CubePage,
  pageScope,
  useCubeSelectionState,
  usePruneStaleContextSelections
} from "../useCubeSelectionState";
import { useForecastSummaryGridState } from "../useForecastSummaryGridState";
import { useSummaryContextOptions } from "./useSummaryContextOptions";
import { type AppliedFilters, useSummaryHandlers } from "./useSummaryHandlers";
import { useSummarySourceSelection } from "./useSummarySourceSelection";

/** Producer options shared by the forecast-summary and budget-summary pages. */
export const SUMMARY_PRODUCER_OPTIONS = [
  { label: "User", value: "user" },
  { label: "Budget", value: "budget" },
  { label: "Other User", value: "other_user" },
  { label: "Analytics", value: "analytics" }
];

/** Configuration for the shared summary page state hook. */
export interface UseSummaryPageStateParams {
  /** Which summary page this is; its selections are remembered per page. */
  page: Extract<CubePage, "forecast-summary" | "budget-summary">;
}

/**
 * Drops a retained grid whose cube the catalogue no longer offers — deleted
 * on another page. Mirrors the temporal grid's
 * ``clearForecastDataIfLoadedFrom``: the grid must not come back saveable
 * against a cube that is gone.
 */
function useDropDeletedCubeGrid(
  appliedFilters: AppliedFilters | null,
  source: {
    isVersionOffered: (
      producer: string,
      version: string
    ) => boolean | undefined;
  },
  grid: { clearLoadedData: () => void; resetIntake: () => void },
  setAppliedFilters: (filters: AppliedFilters | null) => void
) {
  const offered = appliedFilters
    ? source.isVersionOffered(appliedFilters.producer, appliedFilters.version)
    : undefined;
  const { clearLoadedData, resetIntake } = grid;
  useEffect(() => {
    if (offered !== false) return;
    clearLoadedData();
    resetIntake();
    setAppliedFilters(null);
  }, [offered, clearLoadedData, resetIntake, setAppliedFilters]);
}

/**
 * Shared state and logic for the forecast and budget summary pages.
 * Consolidates producer/version/year selection, index source, dimension
 * selectors, context options, apply/save handlers, and grid state.
 */
export function useSummaryPageState({ page }: UseSummaryPageStateParams) {
  const selection = useCubeSelectionState(page);
  const {
    selectedContextOptions,
    setSelectedContextOptions,
    selectedStoreStatuses,
    setSelectedStoreStatuses
  } = selection;
  // Retained with the grid they describe (``useSummaryGridData``).
  const [appliedFilters, setAppliedFilters] =
    useRetainedState<AppliedFilters | null>(
      pageScope(page),
      "appliedFilters",
      null
    );

  const source = useSummarySourceSelection({ page, selectedContextOptions });
  const contextOptions = useSummaryContextOptions({
    cubeId: source.cubeId,
    selectedContextOptions,
    selectedStoreStatuses,
    appliedFilters,
    producer: source.producer,
    version: source.version,
    year: source.year,
    tableDim: source.tableDim,
    rowDim: source.rowDim,
    idxProducer: source.idxProducer,
    idxVersion: source.idxVersion,
    idxYear: source.idxYear
  });

  usePruneStaleContextSelections(contextOptions, setSelectedContextOptions);
  const contextViews = useContextViewState({ page, selection });

  const grid = useForecastSummaryGridState({
    page,
    appliedRowDim: appliedFilters?.rowDim ?? source.rowDim
  });
  useDropDeletedCubeGrid(appliedFilters, source, grid, setAppliedFilters);
  const views = useSummaryViewState({
    page,
    display: grid,
    dims: source
  });

  const handlers = useSummaryHandlers({
    selectedContextOptions,
    resolvedStoreLocations: contextOptions.resolvedStoreLocations,
    source,
    grid,
    appliedFilters,
    setAppliedFilters
  });

  // Selected lookups (derived from source options)
  const selectedProducer = SUMMARY_PRODUCER_OPTIONS.find(
    (option) => option.value === source.producer
  );
  const selectedVersion = source.mainVersionOptions.find(
    (option) => option.value === source.version
  );
  const selectedYear = source.yearOptions.find(
    (option) => option.value === source.year
  );
  const selectedIdxVersion = source.idxVersionOptions.find(
    (option) => option.value === source.idxVersion
  );
  const selectedIdxYear = source.idxYearOptions.find(
    (option) => option.value === source.idxYear
  );
  const selectedTableDim = source.tableDimOptions.find(
    (option) => option.value === source.tableDim
  );
  const selectedRowDim = source.rowDimOptions.find(
    (option) => option.value === source.rowDim
  );

  return {
    selectedContextOptions,
    setSelectedContextOptions,
    ...contextOptions,
    ...source,
    selectedProducer,
    selectedVersion,
    selectedYear,
    selectedIdxVersion,
    selectedIdxYear,
    selectedTableDim,
    selectedRowDim,
    selectedStoreStatuses,
    setSelectedStoreStatuses,
    ...handlers,
    appliedFilters,
    grid,
    views,
    contextViews,
    gridData: grid.gridData,
    tableLabels: grid.tableLabels,
    filteredData: grid.filteredData,
    filteredRowLabelsPerTable: grid.filteredRowLabelsPerTable
  };
}
